/**
 * O formulário que devolve uma trilha ao cliente com o texto do que precisa mudar.
 *
 * **Apontar pendência É devolver a trilha, e os dois atos são um só no servidor.** Não existe
 * "avisar sem reabrir": a trilha congela no envio, e um aviso que não desfaz o congelamento pede
 * ao cliente uma correção que ele não consegue fazer. É por isso que este formulário não tem
 * caixinha de "devolver também" — a decisão não é da tela, é do modelo.
 *
 * **Ele é desenhado dentro do cartão da trilha que vai voltar.** Mesmo quando o gesto começou na
 * divergência entre trilhas, o formulário aparece na trilha alvo: é lá que o consultor vê o
 * estado mudar de "Enviado" para "Em preenchimento" quando confirma, e ver o efeito no mesmo
 * lugar do gesto é o que dispensa recarregar a página para acreditar.
 *
 * **O piso de texto é conferido aqui e no servidor.** A descrição é o que a esteira do cliente
 * exibe; recusar só no servidor gastaria uma ida e deixaria a pessoa achando que o portal falhou.
 */

import { useState } from "react";
import { Aviso, Botao, CampoLongo } from "../ui";
import {
  MAXIMO_DA_PENDENCIA,
  MINIMO_DA_PENDENCIA,
  pendenciaEstaEscrita,
  type AcaoDaPendencia,
} from "./pendencia";

export interface PropsDoFormularioDaPendencia {
  acao: AcaoDaPendencia;
  /** O enunciado da pergunta apontada, quando a pendência nasceu de uma divergência. */
  enunciadoDaPergunta?: string;
}

export function FormularioDaPendencia({
  acao,
  enunciadoDaPergunta,
}: PropsDoFormularioDaPendencia) {
  const [descricao, setDescricao] = useState("");
  const curta = descricao.trim().length > 0 && !pendenciaEstaEscrita(descricao);

  return (
    <div className="pendencia-em-curso" data-testid="formulario-da-pendencia">
      <p className="cartao__texto">
        <b>Devolver esta trilha ao cliente.</b> Ela volta a aceitar resposta e o texto abaixo
        aparece na esteira dele como pendência.
        {acao.alvo?.perguntaId ? (
          <>
            {" "}
            Apontada na pergunta{" "}
            {enunciadoDaPergunta ? (
              <>&ldquo;{enunciadoDaPergunta}&rdquo;</>
            ) : (
              <code>{acao.alvo.perguntaId}</code>
            )}
            .
          </>
        ) : (
          <> A pendência vale para a trilha inteira.</>
        )}
      </p>

      <CampoLongo
        rotulo="O que precisa mudar"
        rows={4}
        value={descricao}
        maxLength={MAXIMO_DA_PENDENCIA}
        onChange={(evento) => setDescricao(evento.target.value)}
        erro={curta ? `Escreva pelo menos ${MINIMO_DA_PENDENCIA} caracteres.` : undefined}
        ajuda="Quem lê é o cliente, na esteira dele. Diga o que está divergente e o que você espera de volta."
        data-testid="descricao-da-pendencia"
      />

      {acao.erro ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-da-pendencia">
          {acao.erro}
        </Aviso>
      ) : null}

      <div className="acoes-do-console">
        <Botao
          tamanho="pequeno"
          data-testid="confirmar-pendencia"
          disabled={acao.enviando || !pendenciaEstaEscrita(descricao)}
          onClick={() => acao.apontar(descricao)}
        >
          {acao.enviando ? "Devolvendo…" : "Devolver ao cliente"}
        </Botao>
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          data-testid="cancelar-pendencia"
          disabled={acao.enviando}
          onClick={acao.fechar}
        >
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
